import { usePiPWindow } from 'components/PiP/PiPProvider';
import { PipActionTile } from 'components/PiP/PipActionTile';
import { selectBlockedMediaPlayback } from 'features/streaming/streamingSlice';
import { enableMediaPlaybackClicked } from 'features/streaming/thunks/enableMediaPlaybackClicked';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from 'store/hooks';

/**
 * Full-window overlay shown inside the Document PiP window while remote media playback
 * is blocked by the browser's autoplay policy. The PiP window is never auto-closed;
 * recovery is realm-aware instead:
 *
 * - Audio and main-document video (screenshare/content — PiP renders only the grid) can
 *   only be unblocked by a gesture in the main tab, so the action returns the user there.
 *   `closePipWindow` focuses the opener — allowed here because the click is a gesture.
 * - Video elements living in the PiP document are unblocked in place: the click is a
 *   PiP-realm gesture, which is exactly the activation their play() needs.
 *
 * Priority when several kinds are blocked at once: audio, then main-document video —
 * both force the leave-PiP decision, and their click also retries the PiP-realm video
 * via the capture-phase listener (useRetryBlockedMediaOnPipGesture) — then PiP video.
 */
export const PipBlockedMediaOverlay = () => {
  const { t } = useTranslation('room');
  const dispatch = useAppDispatch();
  const { closePipWindow } = usePiPWindow();
  const blocked = useAppSelector(selectBlockedMediaPlayback);

  if (blocked.audio) {
    return (
      <PipActionTile
        floating
        message={t('grid.pip_blocked_audio')}
        actionLabel={t('grid.pip_blocked_audio_action')}
        onAction={() => closePipWindow()}
      />
    );
  }

  if (blocked.mainDocumentVideo) {
    return (
      <PipActionTile
        floating
        message={t('grid.pip_blocked_screenshare')}
        actionLabel={t('grid.pip_blocked_screenshare_action')}
        onAction={() => closePipWindow()}
      />
    );
  }

  if (blocked.pipDocumentVideo) {
    return (
      <PipActionTile
        floating
        message={t('grid.pip_blocked_video')}
        actionLabel={t('grid.pip_blocked_video_action')}
        onAction={() => dispatch(enableMediaPlaybackClicked())}
      />
    );
  }

  return null;
};
